import { useState } from "react";
import { ShieldCheck, Pencil, Lock, History } from "lucide-react";
import styles from "./VatSettings.module.css";
import EditVatSettingModal from "../../../../components/Finance/Payments/EditVatSettingModal";

const SEPARATE_SERVICES_DATA = [
  { id: 1, product: "Flights", handling: "VAT Exempt", euVat: "0", nonEuVat: "0", editable: false, remarks: "Passenger transport exemption" },
  { id: 2, product: "Hotels", handling: "VAT by Location", euVat: "Derived", nonEuVat: "Derived", editable: false, remarks: "Via Geo Data" },
  { id: 3, product: "Transfers", handling: "Normal VAT", euVat: "21", nonEuVat: "0", editable: true, remarks: "Place of supply rules" },
  { id: 4, product: "Car Rental", handling: "Normal VAT", euVat: "21", nonEuVat: "0", editable: true, remarks: "Vehicle availability" },
  { id: 5, product: "Visa", handling: "Administrative Service", euVat: "21", nonEuVat: "21", editable: true, remarks: "Belgian VAT" },
];

const PACKAGE_SERVICES_DATA = [
  { id: 6, scenario: "Intermediary + BE Agency", description: "Belgian intermediary", vat: "0", editable: true },
  { id: 7, scenario: "Intermediary + EU Agency", description: "EU intermediary", vat: "21", editable: true },
  { id: 8, scenario: "Tour Operator — EU Destination", description: "TOMS margin", vat: "21", editable: true },
  { id: 9, scenario: "Tour Operator — Non-EU Destination", description: "TOMS exempt", vat: "0", editable: true },
];

const MOCK_AUDIT_LOG = [
  { id: 1, setting: "SEPARATE_TRANSFERS_EU_VAT", oldValue: "20", newValue: "21", reason: "Belgian VAT rate updated per fiscal year 2025", changedBy: "Admin", date: "12 Mar 2026, 14:35" },
  { id: 2, setting: "PACKAGE_TOUR_OP_EU_VAT", oldValue: "19", newValue: "21", reason: "Aligned with new EU TOMS directive", changedBy: "Admin", date: "08 Feb 2026, 09:12" },
];

const formatValue = (val) => {
  if (!val && val !== 0 && val !== "0") return "—";
  if (val === "Derived") return "Derived";
  return `${val}%`;
};

const VatSettings = () => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSetting, setEditSetting] = useState(null);
  const [showAuditLog, setShowAuditLog] = useState(false);

  const handleEdit = (setting, field) => {
    setEditSetting({ ...setting, field });
    setShowEditModal(true);
  };

  const handleSave = (payload) => {
    console.log("Save VAT setting:", editSetting, payload);
    setShowEditModal(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.titleSection}>
          <div className={styles.title}>VAT Settings</div>
          <span className={styles.subtitle}>VAT engine configuration and governance rules</span>
        </div>
        <button
          className={styles.auditBtn}
          onClick={() => setShowAuditLog(!showAuditLog)}
        >
          <History size={15} />
          {showAuditLog ? "Hide Audit Log" : "View Audit Log"}
        </button>
      </div>

      <div className={styles.warningBar}>
        <span className={styles.warningIcon} aria-hidden>
          <ShieldCheck size={18} />
        </span>
        <div className={styles.warningContent}>
          <span className={styles.warningTitle}>VAT Architecture — Binding Rules</span>
          <span className={styles.warningText}>
            Users cannot manually choose VAT regime. VAT regime is system-derived via the decision tree.
            This page configures only VAT parameters — changes apply to future calculations only.
          </span>
        </div>
      </div>

      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>Separate Services — VAT per Product & Destination</span>
        <span className={styles.sectionBadge}>7.4.2</span>
      </div>

      <div className={styles.tableSection}>
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>PRODUCT TYPE</th>
                <th>VAT HANDLING</th>
                <th>EU VAT %</th>
                <th>NON-EU VAT %</th>
                <th>EDITABLE</th>
                <th>REMARKS</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {SEPARATE_SERVICES_DATA.map((row) => (
                <tr key={row.id}>
                  <td className={styles.productName}>{row.product}</td>
                  <td>
                    <span className={styles.handlingBadge}>{row.handling}</span>
                  </td>
                  <td className={styles.vatValue}>{formatValue(row.euVat)}</td>
                  <td className={styles.vatValue}>{formatValue(row.nonEuVat)}</td>
                  <td>
                    {row.editable ? (
                      <span className={styles.editableYes}>Yes</span>
                    ) : (
                      <span className={styles.editableNo}>
                        <Lock size={12} /> No
                      </span>
                    )}
                  </td>
                  <td className={styles.remarks}>{row.remarks}</td>
                  <td className={styles.rowActions}>
                    {row.editable && (
                      <>
                        <button
                          className={styles.editBtn}
                          onClick={() => handleEdit(row, "EU")}
                          title="Edit EU VAT"
                        >
                          <Pencil size={13} /> EU
                        </button>
                        <button
                          className={styles.editBtn}
                          onClick={() => handleEdit(row, "Non-EU")}
                          title="Edit Non-EU VAT"
                        >
                          <Pencil size={13} /> Non-EU
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.ruleCards}>
        <div className={styles.ruleCard}>
          <span className={styles.ruleIcon}>🏨</span>
          <div>
            <span className={styles.ruleTitle}>Hotel VAT — Binding Rule</span>
            <span className={styles.ruleText}>VAT is always determined by hotel country via Geo Data. Manual override is not allowed.</span>
          </div>
        </div>
        <div className={styles.ruleCard}>
          <span className={styles.ruleIcon}>🛡️</span>
          <div>
            <span className={styles.ruleTitle}>Insurance — Hard Rule</span>
            <span className={styles.ruleText}>Insurance is excluded from VAT configuration entirely.</span>
          </div>
        </div>
      </div>

      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>Package Service — VAT on Total Profit Margin</span>
        <span className={styles.sectionBadge}>7.4.3</span>
      </div>

      <div className={styles.tableSection}>
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>SCENARIO</th>
                <th>DESCRIPTION</th>
                <th>VAT %</th>
                <th>EDITABLE</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {PACKAGE_SERVICES_DATA.map((row) => (
                <tr key={row.id}>
                  <td className={styles.productName}>{row.scenario}</td>
                  <td className={styles.remarks}>{row.description}</td>
                  <td className={styles.vatValue}>{formatValue(row.vat)}</td>
                  <td>
                    {row.editable ? (
                      <span className={styles.editableYes}>Yes</span>
                    ) : (
                      <span className={styles.editableNo}>
                        <Lock size={12} /> No
                      </span>
                    )}
                  </td>
                  <td className={styles.rowActions}>
                    {row.editable && (
                      <button
                        className={styles.editBtn}
                        onClick={() => handleEdit(row, "VAT")}
                        title="Edit VAT"
                      >
                        <Pencil size={13} /> Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.ruleCards}>
        <div className={styles.ruleCard}>
          <span className={styles.ruleIcon}>📦</span>
          <div>
            <span className={styles.ruleTitle}>Package VAT — Binding Rule</span>
            <span className={styles.ruleText}>VAT never applies per product. Always booking-level margin. Determined by the VAT decision tree.</span>
          </div>
        </div>
      </div>

      {showAuditLog && (
        <>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>VAT Parameter Change Log</span>
            <span className={styles.sectionBadge}>Audit</span>
          </div>

          <div className={styles.tableSection}>
            <div className={styles.tableCard}>
              {MOCK_AUDIT_LOG.length === 0 ? (
                <div className={styles.emptyAudit}>No VAT parameter changes recorded yet.</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>SETTING</th>
                      <th>OLD VALUE</th>
                      <th>NEW VALUE</th>
                      <th>REASON</th>
                      <th>CHANGED BY</th>
                      <th>DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MOCK_AUDIT_LOG.map((log) => (
                      <tr key={log.id}>
                        <td className={styles.auditKey}>{log.setting}</td>
                        <td className={styles.oldValue}>{log.oldValue}%</td>
                        <td className={styles.newValue}>{log.newValue}%</td>
                        <td className={styles.remarks}>{log.reason}</td>
                        <td>{log.changedBy}</td>
                        <td className={styles.auditDate}>{log.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      <EditVatSettingModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        setting={editSetting}
        onSave={handleSave}
        saveLoading={false}
      />
    </div>
  );
};

export default VatSettings;